/**
 * Triggered-but-Declined · Handover Drop-offs tile.
 *
 * Surfaces the count of safety handovers that were *triggered* by the
 * empathic-handover analyzer but then *declined* by the user at the
 * consent prompt. Master-admin oversight for crisis follow-up gaps.
 *
 * Backend: GET /api/admin/safety/handovers
 *   - declined_total       — count of audit_logs rows with action="handover_declined"
 *   - triggered_total      — declined + accepted
 *   - decline_rate         — declined_total / triggered_total
 *   - declined_recent[]    — latest declined events (user_id, crisis_score, channel, timestamp)
 *
 * Clicking the tile deep-links to /admin/trust-safety so the operator
 * can drill into the full event timeline.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserX, AlertTriangle, RefreshCcw, Loader2, ArrowRight } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toISOString().slice(0, 10);
  } catch {
    return iso.slice(0, 16);
  }
};

const TriggeredDeclinedTile = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request('/api/admin/safety/handovers?limit=5');
      setData(resp);
    } catch (e) {
      if ((e?.message || '').includes('admin_only') || (e?.message || '').includes('403')) {
        setData('hidden');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (data === 'hidden') return null;

  const declined = data?.declined_total ?? 0;
  const triggered = data?.triggered_total ?? 0;
  const accepted = Math.max(triggered - declined, 0);
  const rate = data?.decline_rate ?? 0;
  const ratePct = Math.round(rate * 100);
  const recent = data?.declined_recent || [];

  // Colour scale: green when no declines, amber for moderate, red when
  // half or more triggered handovers are being refused.
  const healthy = declined === 0;
  const critical = triggered > 0 && rate >= 0.5;
  const accent = healthy ? '#10b981' : critical ? '#dc2626' : '#f59e0b';
  const accentBg = healthy ? 'bg-emerald-50' : critical ? 'bg-red-50' : 'bg-amber-50';
  const acceptedPct = triggered > 0 ? (accepted / triggered) * 100 : 0;
  const declinedPct = triggered > 0 ? (declined / triggered) * 100 : 0;

  return (
    <div
      className="bg-white rounded-lg shadow-sm px-5 py-4"
      style={{ border: '1px solid #E0E0E0' }}
      data-testid="triggered-declined-tile"
    >
      <div className="flex flex-wrap items-start gap-5">
        {/* Title + icon */}
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accentBg}`}>
            {healthy
              ? <UserX size={18} style={{ color: accent }} />
              : <AlertTriangle size={18} style={{ color: accent }} />}
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>
              Triggered-but-Declined · Handover Drop-offs
            </h3>
            <p className="text-xs" style={{ color: '#44403A' }}>
              Empathic-handover offers refused at the consent prompt
            </p>
          </div>
        </div>

        {/* Headline metric */}
        <div className="flex items-baseline gap-1.5" data-testid="triggered-declined-total">
          <span className="text-3xl font-bold" style={{ color: accent }}>
            {loading ? '—' : declined}
          </span>
          <span className="text-xs font-medium" style={{ color: '#44403A' }}>
            of {loading ? '—' : triggered} triggered
          </span>
        </div>

        {/* Decline rate badge */}
        <div
          className="px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: `${accent}15`, color: accent }}
          data-testid="triggered-declined-rate"
        >
          {loading ? '—' : `${ratePct}% decline rate`}
        </div>

        {/* Stacked accept-vs-decline bar */}
        <div className="flex-1 min-w-[200px]">
          {loading ? (
            <div className="h-9 flex items-center text-xs" style={{ color: '#6B6257' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> loading…
            </div>
          ) : triggered === 0 ? (
            <div className="text-xs italic" style={{ color: '#6B6257' }}>
              No handovers triggered yet.
            </div>
          ) : (
            <>
              <div className="flex justify-between text-[10px] mb-1" style={{ color: '#666' }}>
                <span data-testid="td-accepted-label">{accepted} accepted</span>
                <span data-testid="td-declined-label">{declined} declined</span>
              </div>
              <div
                className="w-full h-2.5 rounded-full overflow-hidden flex"
                style={{ backgroundColor: '#F3F4F6' }}
                title={`${acceptedPct.toFixed(0)}% accepted · ${declinedPct.toFixed(0)}% declined`}
                data-testid="triggered-declined-bar"
              >
                <div
                  className="h-full"
                  style={{ width: `${acceptedPct}%`, backgroundColor: '#10b981' }}
                />
                <div
                  className="h-full"
                  style={{ width: `${declinedPct}%`, backgroundColor: accent }}
                />
              </div>
            </>
          )}
        </div>

        {/* Reload + jump */}
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
            title="Reload"
            data-testid="triggered-declined-reload"
            disabled={loading}
          >
            <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => navigate('/admin/safety')}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white"
            style={{ backgroundColor: accent }}
            data-testid="triggered-declined-drilldown"
            title="Open Safety & Guardrails"
          >
            Investigate <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* Recent decline strip (only when we actually have declines) */}
      {!loading && recent.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: '#F1F1F1' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#6B6257' }}>
            Recent declines
          </p>
          <div className="flex flex-wrap gap-2" data-testid="triggered-declined-recent">
            {recent.slice(0, 5).map((ev, i) => {
              const cs = ev?.metadata?.crisis_score;
              const csNum = typeof cs === 'number' ? cs : null;
              const csColor = csNum === null ? '#9CA3AF' : csNum >= 0.7 ? '#DC2626' : csNum >= 0.4 ? '#F59E0B' : '#10b981';
              return (
                <div
                  key={ev?.log_id || i}
                  className="flex items-center gap-2 px-2.5 py-1 rounded-md"
                  style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}
                  data-testid={`triggered-declined-row-${i}`}
                  title={`channel: ${ev?.metadata?.channel || 'n/a'}`}
                >
                  <span className="text-[10px] font-mono" style={{ color: '#666' }}>
                    {(ev?.user_id || 'anon').slice(0, 8)}***
                  </span>
                  {csNum !== null && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${csColor}15`, color: csColor }}
                    >
                      crisis {csNum.toFixed(2)}
                    </span>
                  )}
                  <span className="text-[10px]" style={{ color: '#6B6257' }}>
                    {fmtTime(ev?.timestamp)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggeredDeclinedTile;
